import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CopilotContext = createContext(null);

export function useCopilot() {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error("useCopilot must be used inside <CopilotProvider>");
  return ctx;
}

const STORAGE_KEY = "peak:copilot_open";
const DEMO_STORAGE_KEY = "peak:copilot_demo";

export function CopilotProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [demo, setDemo] = useState(false);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const autoOpenedRef = useRef(false);
  const autoNavigatedRef = useRef(false);

  // Load the correct session (demo or real) whenever `demo` toggles or drawer opens
  const loadSession = useCallback(async (isDemo = demo) => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.get(`/copilot/session?demo=${isDemo ? "1" : "0"}`);
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setSession(null);
    } finally {
      setBusy(false);
    }
  }, [user, demo]);

  // Silent poll — same GET but does NOT flip the `busy` spinner. Used
  // during streaming generation so the UI stays interactive while cards
  // trickle in every second.
  const pollSession = useCallback(async (isDemo = demo) => {
    if (!user) return;
    try {
      const { data } = await api.get(`/copilot/session?demo=${isDemo ? "1" : "0"}`);
      setSession(data);
    } catch { /* ignore poll errors */ }
  }, [user, demo]);

  // Poll every 1.5s while generation is in progress
  useEffect(() => {
    if (!session) return;
    if (session.state !== "generating_recs") return;
    const t = setInterval(() => { pollSession(demo); }, 1500);
    return () => clearInterval(t);
  }, [session, demo, pollSession]);

  // Auto-open once per session for brand-new users who haven't finished or dismissed onboarding
  useEffect(() => {
    if (!user || autoOpenedRef.current) return;
    // Skip auto-open for superadmins (they only get it via the demo trigger)
    // and for anyone impersonating another user.
    if (user.is_superadmin || user.impersonating) return;
    if (user.onboarding_completed || user.onboarding_dismissed) return;
    // Respect persistent open state (e.g. user closed it manually earlier)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "0") return;
    } catch { /* ignore */ }
    autoOpenedRef.current = true;
    setOpen(true);
  }, [user]);

  // Restore demo mode across page reloads (superadmin only)
  useEffect(() => {
    if (!user?.is_superadmin) return;
    try {
      const stored = localStorage.getItem(DEMO_STORAGE_KEY);
      if (stored === "1") setDemo(true);
    } catch { /* ignore */ }
  }, [user]);

  // Fetch the session whenever the drawer opens
  useEffect(() => {
    if (open && user) loadSession(demo);
  }, [open, demo, user, loadSession]);

  const openCopilot = useCallback((opts = {}) => {
    if (opts.demo && !user?.is_superadmin) return;
    if (opts.demo !== undefined) {
      setDemo(!!opts.demo);
      try { localStorage.setItem(DEMO_STORAGE_KEY, opts.demo ? "1" : "0"); } catch { /* ignore */ }
    }
    setOpen(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  }, [user]);

  const closeCopilot = useCallback(() => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "0"); } catch { /* ignore */ }
  }, []);

  const sendMessage = useCallback(async (message) => {
    if (!message?.trim()) return;
    // Optimistic append so the UI feels instant
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [
          ...(prev.messages || []),
          { role: "user", content: message, at: new Date().toISOString() },
        ],
      };
    });
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/message", { message, demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const submitWebsite = useCallback(async (url) => {
    if (!url?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/research", { url, demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const verify = useCallback(async (approved, edits = null) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/verify", { approved, edits, demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const dismiss = useCallback(async () => {
    try { await api.post("/copilot/dismiss", { demo }); } catch { /* best effort */ }
    closeCopilot();
  }, [demo, closeCopilot]);

  // ---- Wave 2: recommendation walkthrough ----
  const approveRec = useCallback(async (recId) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/copilot/recommendations/${recId}/approve`, { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const skipRec = useCallback(async (recId) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/copilot/recommendations/${recId}/skip`, { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const explainRec = useCallback(async (recId) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/copilot/recommendations/${recId}/explain`, { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const editRec = useCallback(async (recId, patch) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.patch(`/copilot/recommendations/${recId}`, { ...patch, demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const finishWalkthrough = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/recommendations/finish", { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const regenerateRecs = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/recommendations/regenerate", { demo });
      setSession(data);
      autoNavigatedRef.current = false; // allow re-navigation to the plan page
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  // ---- Walkthrough narration ----
  const narrateStart = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/recommendations/narrate/start", { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const narrateNext = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/recommendations/narrate/next", { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  const narrateStop = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/recommendations/narrate/stop", { demo });
      setSession(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [demo]);

  // Auto-navigate to the recommendations plan page as soon as generation
  // starts (state='generating_recs') OR when recs first arrive. Fires once
  // per session arrival so we don't fight the user.
  useEffect(() => {
    if (!session) return;
    if (autoNavigatedRef.current) return;
    const hasRecs = (session.recommendations?.length || 0) > 0;
    const isStreaming = session.state === "generating_recs";
    if (!hasRecs && !isStreaming) return;
    if (session.state === "completed") return;
    if (location.pathname.startsWith("/copilot/recommendations")) {
      autoNavigatedRef.current = true;
      return;
    }
    if (!open) return;
    autoNavigatedRef.current = true;
    const q = demo ? "?demo=1" : "";
    navigate(`/copilot/recommendations${q}`);
  }, [session, open, demo, location.pathname, navigate]);

  const startDemo = useCallback(async () => {
    if (!user?.is_superadmin) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/copilot/demo/start");
      setDemo(true);
      try { localStorage.setItem(DEMO_STORAGE_KEY, "1"); } catch { /* ignore */ }
      setSession(data);
      setOpen(true);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [user]);

  const stopDemo = useCallback(async () => {
    try { await api.delete("/copilot/demo"); } catch { /* best effort */ }
    setDemo(false);
    try { localStorage.setItem(DEMO_STORAGE_KEY, "0"); } catch { /* ignore */ }
    setSession(null);
    closeCopilot();
  }, [closeCopilot]);

  const resetOnboarding = useCallback(async () => {
    if (!user?.is_superadmin) return;
    try {
      await api.post(`/copilot/reset?demo=${demo ? "1" : "0"}`);
      await loadSession(demo);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [user, demo, loadSession]);

  const value = useMemo(() => ({
    open, demo, session, busy, error,
    isSuperadmin: !!user?.is_superadmin,
    openCopilot, closeCopilot,
    sendMessage, submitWebsite, verify, dismiss,
    approveRec, skipRec, explainRec, editRec, finishWalkthrough, regenerateRecs,
    narrateStart, narrateNext, narrateStop,
    startDemo, stopDemo, resetOnboarding,
    reload: () => loadSession(demo),
  }), [
    open, demo, session, busy, error, user,
    openCopilot, closeCopilot,
    sendMessage, submitWebsite, verify, dismiss,
    approveRec, skipRec, explainRec, editRec, finishWalkthrough, regenerateRecs,
    narrateStart, narrateNext, narrateStop,
    startDemo, stopDemo, resetOnboarding, loadSession,
  ]);

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}
